import OptionModal from '../../../../../../components/modals/option-modal';
import { FIELD_STORED, useTable } from './use-synchronize';
import { FieldForSync, useFieldsForMapping } from './synchronize-context';
import React, { ChangeEvent, useCallback, useState } from 'react';
import Table from '../../../../../../components/common/table/table';
import { map } from 'rxjs/operators';
import css from './tables.module.css';
import { FieldForMapping } from './mapping-table';
import { FieldStatus } from '../../../../../../enums/field-status';
import Button from './button';
import Logo from './logo';
import { nanoid } from '@reduxjs/toolkit';
import { ReactComponent as EditIcon } from './icons/edit.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addNotice } from '../../../../../../store/global-slice';
import { route } from '../../../../../../services/utils';
import { CROP_FIELDS_AND_WORKS_INDEX } from '../../../../../../router/routes';
import { Section } from '../../..';

interface CurrentStatusWithLadFieldResponse extends FieldForSync {
  new_field_from_lad?: boolean;
}

interface LadSyncRequest {
  mapped: {
    save: Required<
      Pick<FieldForSync, 'geometry' | 'id'> & {
        group_id: CurrentStatusWithLadFieldForMapping['group_id'];
      }
    >[];
  };
  lad: {
    update: Required<Pick<FieldForSync, 'name' | 'geometry'>>[];
  };
  local: {
    update: FieldForSync['id'][];
  };
}

export interface CurrentStatusWithLadFieldForMapping
  extends FieldForMapping,
    CurrentStatusWithLadFieldResponse {
  key: string;
}

const getClassNames = (row: CurrentStatusWithLadFieldForMapping) => {
  const conditions = [
    {
      isValid: row.group_id && row.id,
      className: () => {
        const classNames = [
          'colored',
          'colored-current',
          row.first_local_field ? 'colored-first' : '',
          row.last_local_field ? 'border' : '',
        ];
        return classNames.map((className) => css[className]).join(' ');
      },
    },
    {
      isValid: row.new_field_from_lad,
      className: () => `${css['new-field']} ${css['not-editable']}`,
    },
    {
      isValid: row.field_status === FieldStatus.CREATED_MANUALLY,
      className: () => css['new-field'],
    },
  ];

  for (const condition of conditions) {
    if (condition.isValid) {
      return condition.className();
    }
  }

  return `${css['not-editable']} ${css['default']}`;
};

const newFieldFromLad = (
  <div className={`w-4 h-4 ${css['new-field-from-lad']}`}></div>
);

export const newFieldFromSystem = (
  <div className={`w-4 h-4  ${css['new-field-from-system']}`}></div>
);

const processFieldsForMapping = (
  fields: CurrentStatusWithLadFieldResponse[]
): CurrentStatusWithLadFieldForMapping[] => {
  const fieldsWithStoredLocation = fields.map((field) => {
    const stored = field.id ? FIELD_STORED.LOCAL : FIELD_STORED.LAD;
    return { ...field, stored };
  });

  const markFirstAndLastLocalField = fieldsWithStoredLocation.map(
    (field, index) => {
      const isLocalField = field.stored === FIELD_STORED.LOCAL;

      if (isLocalField) {
        const isNextFieldLad =
          index + 1 < fieldsWithStoredLocation.length &&
          fieldsWithStoredLocation[index + 1].stored === FIELD_STORED.LAD;

        const isFirstLocalField =
          index === 0 ||
          fieldsWithStoredLocation[index - 1].stored !== FIELD_STORED.LOCAL;
        return {
          ...field,
          first_local_field: isFirstLocalField,
          last_local_field: isNextFieldLad,
        };
      } else {
        return {
          ...field,
          first_local_field: false,
          last_local_field: false,
        };
      }
    }
  );

  return markFirstAndLastLocalField.map(
    ({
      new_field_from_lad,
      stored,
      geometry,
      group_id,
      first_local_field,
      ...field
    }) => {
      let fieldStored: CurrentStatusWithLadFieldForMapping['stored'] =
        FIELD_STORED.LAD;

      const conditions = [
        {
          check: stored === FIELD_STORED.LOCAL && group_id,
          component: <Logo />,
        },
        {
          check: stored === FIELD_STORED.LOCAL,
          component: newFieldFromSystem,
        },
        {
          check: new_field_from_lad,
          component: newFieldFromLad,
        },
      ];

      for (const condition of conditions) {
        if (condition.check) {
          fieldStored = condition.component;
          break;
        }
      }

      return {
        ...field,
        geometry,
        first_local_field,
        ...(group_id ? { group_id } : {}),
        ...(new_field_from_lad ? { new_field_from_lad } : {}),
        editable: false,
        stored: fieldStored,
        key: nanoid(),
      };
    }
  );
};

const filterFieldsWithSameGeometry = ({
  fields,
  state,
  mappingState,
}: {
  fields: CurrentStatusWithLadFieldForMapping[];
  state: CurrentStatusWithLadFieldForMapping[];
  mappingState: FieldForMapping[];
}) => {
  const ladFields = fields.filter(({ geometry, stored }) =>
    [mappingState, state].some((fields) =>
      fields.some(
        (field) => field.geometry === geometry && stored === FIELD_STORED.LAD
      )
    )
  );

  const groupIdsToRemove = fields
    .filter(({ geometry }) =>
      ladFields.some((field) => field.geometry === geometry)
    )
    .map(({ group_id }) => group_id);

  return fields.filter((field) => !groupIdsToRemove.includes(field.group_id));
};

export default function CurrentFieldStatusWithLadTable() {
  const { t, tp, farmId, season, fieldService } = useTable();
  const {
    mappingState,
    setCurrentLadStatusWithLadState,
    setMappingState,
    currentLadStatusWithLadState: { state, editable: editableState },
  } = useFieldsForMapping();
  const [confirmation, setConfirmation] = useState(null);
  const [showInputFor, setShowInputFor] =
    useState<CurrentStatusWithLadFieldForMapping['key']>('');
  const [isUpdating, setIsUpdating] = useState(false);

  const [input, setInput] = useState('');
  const history = useHistory();
  const dispatch = useDispatch();

  const request = useCallback(
    () =>
      fieldService
        .currentStatusWithLad<CurrentStatusWithLadFieldResponse>(farmId, season)
        .pipe(
          map((request) => {
            const fields = processFieldsForMapping(request.data);

            const filteredFields = filterFieldsWithSameGeometry({
              fields,
              state,
              mappingState: mappingState.state,
            });

            setCurrentLadStatusWithLadState((prevState) => ({
              ...prevState,
              initialState: filteredFields.filter(
                (field) => field.stored === FIELD_STORED.LAD || field.group_id
              ),
            }));

            return {
              ...request,
              data: [
                ...state,
                ...filteredFields,
                ...editableState.filter(
                  (field) => field.field_status === FieldStatus.CREATED_MANUALLY
                ),
              ],
            };
          })
        ),
    [
      fieldService,
      farmId,
      season,
      state,
      mappingState.state,
      setCurrentLadStatusWithLadState,
      editableState,
    ]
  );

  const saveButton = (
    <Button
      loading={isUpdating}
      disabled={isUpdating}
      text={tp('create')}
      onClick={() => setConfirmation(confirmationDialog)}
    />
  );

  const importFields = useCallback(() => {
    const data = {
      mapped: {
        save: state
          .map((field) => {
            if (field.id) {
              return { ...field, geometry: null };
            } else {
              return field;
            }
          })
          .map(({ geometry, group_id, id }) => ({
            geometry,
            group_id,
            id,
          })),
      },
      lad: {
        update: editableState
          .filter((field) => !field.id)
          .map(({ name, geometry }) => ({
            geometry,
            name,
          })),
      },
      local: {
        update: editableState
          .filter(
            (field) => field.field_status === FieldStatus.CREATED_MANUALLY
          )
          .map(({ id }) => id),
      },
    };

    const subscription = fieldService
      .synchronizeWithLad<LadSyncRequest>(data, farmId, season)
      .subscribe(
        (_response) => {
          setIsUpdating(false);

          dispatch(
            addNotice({
              accessor: 'ListFields',
              status: 'success',
              description: tp('success'),
            })
          );

          history.push(
            route(CROP_FIELDS_AND_WORKS_INDEX, {
              farmId: farmId,
              season: season,
              tab: Section.fields,
            })
          );
        },
        (_error) => {
          setIsUpdating(false);

          dispatch(
            addNotice({
              accessor: 'ListFields',
              status: 'error',
              description: tp('error'),
            })
          );
          history.push(
            route(CROP_FIELDS_AND_WORKS_INDEX, {
              farmId: farmId,
              season: season,
              tab: Section.fields,
            })
          );
        }
      );
    return () => subscription.unsubscribe();
  }, [
    state,
    editableState,
    fieldService,
    farmId,
    season,
    dispatch,
    tp,
    history,
  ]);

  const confirmationDialog = (
    <OptionModal
      title={tp('confirmation_title')}
      content={tp('confirmation')}
      buttons={[
        { title: t('yes'), name: 'yes', value: true },
        { title: t('no'), name: 'no', value: false },
      ]}
      onChoice={(choice) => {
        setConfirmation(null);
        if (choice === true) {
          setIsUpdating(true);
          importFields();
        }
      }}
      onClose={() => setConfirmation(null)}
    />
  );

  const onMouseEnter = (row: CurrentStatusWithLadFieldForMapping) => {
    setCurrentLadStatusWithLadState((prevState) => ({
      ...prevState,
      hovered: row,
    }));
  };

  const onMouseLeave = () => {
    setCurrentLadStatusWithLadState((prevState) => ({
      ...prevState,
      hovered: null,
    }));
  };

  const onRowClick = (field: CurrentStatusWithLadFieldForMapping) => {
    if (
      field.editable &&
      !field.group_id &&
      field.field_status === FieldStatus.CREATED_MANUALLY
    ) {
      setCurrentLadStatusWithLadState((prevState) => ({
        ...prevState,
        editable: prevState.editable.filter(({ id }) => id !== field.id),
      }));
    } else if (field.editable && field.group_id) {
      setCurrentLadStatusWithLadState((prevState) => ({
        ...prevState,
        state: state.filter(({ group_id }) => field.group_id !== group_id),
      }));

      setMappingState((prevState) => ({
        ...prevState,
        state: [
          ...prevState.state,
          ...state.filter(
            ({ group_id, name }) =>
              group_id === field.group_id && typeof name === 'string'
          ),
        ],
      }));
    }

    return;
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const onEditBtnClick = (field: CurrentStatusWithLadFieldForMapping) => {
    if (showInputFor !== field.key) {
      setInput(field.name);
      setShowInputFor(field.key);
    } else {
      setShowInputFor('');
      setInput('');
    }

    if (input) {
      setCurrentLadStatusWithLadState((prevState) => ({
        ...prevState,
        editable: [...prevState.editable, { ...field, name: input }],
      }));
    }
  };

  const editable = (field: CurrentStatusWithLadFieldForMapping) =>
    field.new_field_from_lad || field.stored === FIELD_STORED.LAD ? (
      <div className="flex gap-2 items-center">
        {showInputFor === field.key ? (
          <input
            aria-label={t('edit')}
            type="text"
            placeholder=""
            onChange={onInputChange}
            value={input}
            autoFocus
            className="max-w-28"
          />
        ) : (
          <span>
            {editableState.find(({ geometry }) => field.geometry === geometry)
              ?.name ?? field.name}
          </span>
        )}
        <button
          onClick={() => onEditBtnClick(field)}
          aria-label={showInputFor === field.key ? t('save') : t('edit')}
        >
          {showInputFor ? (
            showInputFor === field.key ? (
              <FontAwesomeIcon aria-hidden icon={faCheck} />
            ) : null
          ) : (
            <EditIcon aria-hidden />
          )}
        </button>
      </div>
    ) : (
      field.name
    );

  return (
    <div className="flex flex-col">
      <div className="flex w-full items-end justify-between px-4 pt-4">
        <p className="pb-0 uppercase">{tp('current_field_status_with_lad')}</p>
        <div className="flex gap-8 flex-wrap">
          <div className="flex gap-2">
            <div className={`w-4 h-4 ${css['new-field-from-lad']}`}></div>
            <div>{tp('new_field_from_lad')}</div>
          </div>
          <div className="flex gap-2">
            <div className={`w-4 h-4  ${css['new-field-from-system']}`}></div>
            <div>{tp('new_field_from_ml')}</div>
          </div>
        </div>
      </div>
      <Table<CurrentStatusWithLadFieldForMapping>
        noDataMessage={tp('no_added_records')}
        request={request}
        keyAccessor="key"
        paginate={false}
        rowClassSetter={getClassNames}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        columns={[
          {
            accessor: 'stored',
            className: 'text-accent',
            header: '',
          },
          {
            accessor: 'lad_number',
            header: tp('lad_number'),
          },
          {
            accessor: 'name',
            header: tp('name'),
            editable,
          },
          {
            accessor: 'block_number',
            header: tp('block_number'),
          },
          {
            accessor: 'area',
            header: tp('area'),
          },
          {
            accessor: 'crop_value',
            header: tp('crop'),
          },
          {
            accessor: 'editable',
            header: '',
            onRowClick,
          },
        ]}
      />
      <div className="self-end p-4 pt-0">{saveButton}</div>
      {confirmation}
    </div>
  );
}
